const featureItems = [
  ['CAN log analysis', 'Upload captures, inspect raw frames, filter CAN IDs, and export clean CSV or JSON data.'],
  ['AI-assisted decoding', 'Use SmartCAN workflows to organize candidate signals and explain observed CAN data patterns.'],
  ['OBD data interpretation', 'Decode supported generic OBD-II responses such as engine RPM and vehicle speed.'],
  ['Vehicle diagnostics workflow', 'Move from live capture to log review, signal hints, evidence, and repeatable diagnostics.'],
  ['Developer-friendly examples', 'Use browser tools, VS Code workflows, and Python SDK direction for automation-friendly analysis.']
];

const useCases = [
  ['Automotive diagnostics', 'Inspect ECU traffic and OBD responses while keeping raw CAN bus data visible.'],
  ['Reverse engineering', 'Compare changing bytes and candidate signals across repeatable test conditions.'],
  ['Fleet inspection', 'Review captures and export evidence for repeatable inspection workflows.'],
  ['Used car inspection', 'Support deeper automotive data review alongside traditional vehicle diagnostics.'],
  ['Embedded development', 'Debug CAN networks in robotics, industrial systems, and custom controller projects.']
];

const comparisonRows = [
  ['Raw frame visibility', 'Available, often expert-oriented', 'Visible in Analyzer and Live Mode'],
  ['CAN log workflow', 'May require separate tools', 'Browser upload, filter, summary, and export'],
  ['AI-assisted interpretation', 'Usually manual', 'Designed to assist candidate signal analysis'],
  ['Community hints', 'Often private files or forums', 'Verified signal hints shown in product UI'],
  ['Beginner path', 'Can be steep', 'Guided pages, examples, and safe wording around uncertainty']
];

const faqItems = [
  ['What is SmartCAN?', 'SmartCAN is an AI-assisted CAN Bus Analyzer and decoder workflow for live capture, CAN log analysis, OBD data interpretation, and community signal hints.'],
  ['Is SmartCAN only for vehicle diagnostics?', 'No. SmartCAN is useful for automotive diagnostics, embedded systems, robotics, industrial CAN networks, and reverse engineering.'],
  ['What is CAN Bus?', 'CAN Bus is a communication network where ECUs and controllers publish compact message IDs and payload bytes.'],
  ['What is CAN decoding?', 'CAN decoding turns raw CAN IDs and payload bytes into meaningful signal names, values, units, and context.'],
  ['Why is CAN data hard to understand?', 'CAN data is compact, context-specific, and often manufacturer-defined, so the same bytes can mean different things on different systems.'],
  ['Does SmartCAN use AI?', 'SmartCAN is designed to support AI-assisted analysis and organization, while verified signal definitions still require evidence and review.'],
  ['Can SmartCAN decode OBD data?', 'SmartCAN includes generic OBD-II decoding for supported response frames such as RPM and vehicle speed.'],
  ['Can I contribute CAN signal hints?', 'Yes. Users can submit signal hints through the web UI, and reviewed signals can become part of the verified catalog.']
];

export function WhatIsSmartCAN() {
  return (
    <main className="seo-page">
      <section className="section page-shell seo-hero">
        <div>
          <p className="eyebrow">CAN Bus Analyzer guide</p>
          <h1>What Is SmartCAN?</h1>
          <p className="lead">
            SmartCAN is an AI-powered CAN Bus Analyzer and decoder platform designed to help engineers inspect live
            CAN traffic, analyze logs, interpret OBD data, and organize community-verified signal hints.
          </p>
          <div className="hero-actions">
            <a className="button primary" href="/live">Open live demo</a>
            <a className="button secondary" href="/analyzer">Analyze a CAN log</a>
            <a className="button ghost" href="/decode">Browse community signals</a>
          </div>
        </div>
      </section>

      <section className="section seo-content-grid">
        <article className="data-panel">
          <p className="eyebrow">CAN bus basics</p>
          <h2>What is CAN Bus?</h2>
          <p>
            CAN Bus is a communication network used by ECUs, embedded controllers, robotics systems, industrial
            machines, and vehicles. Devices publish compact CAN IDs and payload bytes instead of human-readable data.
          </p>
        </article>
        <article className="data-panel">
          <p className="eyebrow">CAN decoder basics</p>
          <h2>What is CAN decoding?</h2>
          <p>
            CAN decoding maps raw frame IDs and payload bytes into signal names, values, units, and context. Accurate
            decoding depends on definitions such as bit positions, byte order, scaling, offsets, and evidence.
          </p>
        </article>
        <article className="data-panel">
          <p className="eyebrow">Hard problem</p>
          <h2>Why CAN data is hard to understand</h2>
          <p>
            CAN frames are compact and context-specific. The same CAN ID can vary by ECU, vehicle, firmware, bus, or
            embedded system design, so raw captures need careful analysis before exact claims are made.
          </p>
        </article>
      </section>

      <section className="band seo-use-cases">
        <div>
          <p className="eyebrow">How SmartCAN helps</p>
          <h2>From raw frames to useful signal awareness</h2>
          <p>
            SmartCAN helps capture live traffic, inspect uploaded CAN logs, filter candidate signals, decode supported
            OBD responses, and compare observed CAN IDs against verified community hints. It keeps raw data visible so
            engineers can validate what they see.
          </p>
        </div>
        <div className="usecase-grid">
          <span>CAN Bus Analyzer</span>
          <span>CAN Decoder</span>
          <span>OBD Data Analysis</span>
          <span>Vehicle Diagnostics</span>
          <span>AI CAN Bus Analysis</span>
          <span>Automotive Diagnostics Tool</span>
        </div>
      </section>

      <section className="section">
        <div className="section-heading compact">
          <p className="eyebrow">Key features</p>
          <h2>SmartCAN feature areas</h2>
        </div>
        <div className="feature-grid">
          {featureItems.map(([title, body]) => (
            <article className="feature-card" key={title}>
              <span className="card-icon" aria-hidden="true"></span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="section-heading compact">
          <p className="eyebrow">Real-world use cases</p>
          <h2>Where SmartCAN can assist</h2>
        </div>
        <div className="seo-card-grid">
          {useCases.map(([title, body]) => (
            <article className="data-panel" key={title}>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="band">
        <div className="section-heading compact">
          <p className="eyebrow">Comparison</p>
          <h2>SmartCAN vs traditional CAN tools</h2>
          <p>Traditional tools are powerful for experts, while SmartCAN focuses on fast interpretation, workflow, and AI-assisted analysis.</p>
        </div>
        <div className="table-wrap">
          <table className="can-table live comparison-table">
            <thead><tr><th>Area</th><th>Traditional tools</th><th>SmartCAN</th></tr></thead>
            <tbody>
              {comparisonRows.map(([area, traditional, smartcan]) => (
                <tr key={area}><td>{area}</td><td>{traditional}</td><td>{smartcan}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="inline-actions">
          <a className="button secondary" href="/smartcan-vs-alternatives">Compare alternatives</a>
          <a className="button ghost" href="/can-bus-decoder-guide">Read decoder guide</a>
        </div>
      </section>

      <section className="section">
        <div className="section-heading compact">
          <p className="eyebrow">FAQ</p>
          <h2>SmartCAN questions</h2>
        </div>
        <div className="faq-grid">
          {faqItems.map(([question, answer]) => (
            <article className="data-panel faq-card" key={question}>
              <h3>{question}</h3>
              <p>{answer}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
